import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Minus, ChevronRight, Layers, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { api } from "../lib/api";
import SubCategoryTrend from "./SubCategoryTrend";
import LocalStorePicker from "./LocalStorePicker";
import ExportButtons from "./ExportButtons";
import { downloadCSV, downloadPDF, fmt } from "../lib/exports";
import { windowPhrase } from "../lib/compare";
import PeriodBadge from "./PeriodBadge";

function money(v) {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function fullMoney(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function Delta({ pct, small = false }) {
  const size = small ? 10 : 12;
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-0.5 font-mono-bm text-[11px] text-neutral-400">
        <Minus size={size} strokeWidth={2} /> —
      </span>
    );
  }
  const up = pct >= 0;
  const cls = up ? "text-emerald-700" : "text-[#c72c2c]";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono-bm text-[11px] font-semibold ${cls}`}>
      <Icon size={size} strokeWidth={2.5} />
      {up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

/** Lazy-loaded mini SKU table for one expanded category row. */
function CategorySkuTable({ category, range, outletIds }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Default: match backend ordering (revenue desc).
  const [sortKey, setSortKey] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    if (!category || !range) return;
    let cancel = false;
    setLoading(true);
    const params = {
      category,
      start_date: range.start,
      end_date: range.end,
      limit: 40,
    };
    if (outletIds?.length) params.outlet_ids = outletIds.join(",");
    api.get("/products/category-skus", { params })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [category, range?.start, range?.end, outletIds?.join(",")]);

  const rawSkus = data?.skus || [];
  const total = data?.sku_count || 0;

  const skus = useMemo(() => {
    const arr = [...rawSkus];
    const dir = sortDir === "asc" ? 1 : -1;
    // Missing values (e.g. new SKUs with null growth) should sink to the bottom
    // regardless of direction so the user isn't misled by a wall of "—".
    const val = (r) => {
      if (sortKey === "units") return r.units ?? 0;
      if (sortKey === "growth") return r.unit_growth_pct;
      if (sortKey === "avg_price") return r.avg_price ?? 0;
      return r.revenue ?? 0;
    };
    arr.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const aNull = va === null || va === undefined;
      const bNull = vb === null || vb === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      return (va - vb) * dir;
    });
    return arr;
  }, [rawSkus, sortKey, sortDir]);
  const shown = skus.length;

  const clickHeader = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      // Growth defaults to descending (biggest gainers first); avg_price defaults
      // to descending (most expensive first); units/revenue → desc as well.
      setSortDir("desc");
    }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ArrowUpDown size={10} className="inline text-neutral-400" strokeWidth={2} />;
    return sortDir === "desc"
      ? <ArrowDown size={10} className="inline text-[#0a0a0a]" strokeWidth={2.5} />
      : <ArrowUp size={10} className="inline text-[#0a0a0a]" strokeWidth={2.5} />;
  };

  const sortLabel = (label, k, width) => (
    <button
      type="button"
      onClick={() => clickHeader(k)}
      className={`bm-eyebrow inline-flex items-center gap-1 justify-end w-full hover:text-[#0a0a0a] ${
        sortKey === k ? "text-[#0a0a0a] font-semibold" : "text-neutral-500"
      }`}
      title={`Sort by ${label}`}
      data-testid={`cat-sku-sort-${k}`}
    >
      {label} <SortIcon k={k} />
    </button>
  );

  return (
    <div className="pl-3 md:pl-10 pr-3 md:pr-4 py-3" data-testid={`cat-rollup-skus-${category}`}>
      <div className="bm-eyebrow text-neutral-500 mb-2">
        Top {shown} SKUs sorted by <span className="text-[#0a0a0a]">{
          { revenue: "revenue", units: "units", growth: "Δ vs prior", avg_price: "avg $" }[sortKey]
        } · {sortDir}</span>{total > shown ? ` · ${total - shown} more not shown` : ""}
      </div>
      {loading && <div className="text-sm text-neutral-500 py-3">Loading SKUs…</div>}
      {!loading && skus.length === 0 && (
        <div className="bm-eyebrow text-neutral-500 py-3">No SKUs sold in this window.</div>
      )}
      {!loading && skus.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[#0a0a0a]/15">
                <th className="text-left bm-eyebrow text-neutral-500 py-1.5 pr-2 w-8">#</th>
                <th className="text-left bm-eyebrow text-neutral-500 py-1.5 pr-2">SKU-level description</th>
                <th className="text-left bm-eyebrow text-neutral-500 py-1.5 px-2 w-24 hidden md:table-cell">SKU</th>
                <th className="text-right py-1.5 px-2 w-16 hidden md:table-cell">{sortLabel("Units", "units")}</th>
                <th className="text-right py-1.5 px-2 w-20">{sortLabel("Δ vs prior", "growth")}</th>
                <th className="text-right py-1.5 px-2 w-16 hidden md:table-cell">{sortLabel("Avg $", "avg_price")}</th>
                <th className="text-right py-1.5 pl-2 pr-2 w-24">{sortLabel("Revenue", "revenue")}</th>
              </tr>
            </thead>
            <tbody>
              {skus.map((s, j) => (
                <tr key={s.product_id} className="border-b border-[#0a0a0a]/10 last:border-b-0" data-testid={`cat-rollup-sku-${category}-${j}`}>
                  <td className="py-1.5 pr-2 font-mono-bm text-xs text-neutral-500">{String(j + 1).padStart(2, "0")}</td>
                  <td className="py-1.5 pr-2 max-w-[160px] md:max-w-[420px]" title={s.parent_name ? `${s.parent_name} · ${s.name}` : s.name}>
                    <div className="truncate">
                    {s.parent_name && s.parent_name.toUpperCase() !== s.name.toUpperCase()
                      ? <><span className="text-neutral-500">{s.parent_name}</span> · {s.name}</>
                      : s.name}
                    </div>
                    <div className="md:hidden bm-eyebrow text-neutral-500">avg ${s.avg_price}</div>
                  </td>
                  <td className="py-1.5 px-2 font-mono-bm text-xs text-neutral-500 hidden md:table-cell">{s.sku || "—"}</td>
                  <td className={`py-1.5 px-2 text-right font-mono-bm tabular-nums hidden md:table-cell ${sortKey === "units" ? "font-bold" : "font-semibold"}`}>{s.units.toLocaleString()}</td>
                  <td className="py-1.5 px-2 text-right"><Delta pct={s.unit_growth_pct} small /></td>
                  <td className={`py-1.5 px-2 text-right font-mono-bm tabular-nums text-neutral-600 hidden md:table-cell ${sortKey === "avg_price" ? "font-bold text-[#0a0a0a]" : ""}`}>${s.avg_price}</td>
                  <td className={`py-1.5 pl-2 pr-2 text-right font-mono-bm tabular-nums ${sortKey === "revenue" ? "font-bold" : ""}`}>{money(s.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Expandable Categories panel — top-level Lightspeed categories with a
 * weekly per-store trend chart AND a mini SKU table on click. T-Shirts
 * are split into Printed / Vintage / Heat-Transfer; Printed T's is
 * intentionally not clickable since it has its own dedicated page.
 */
export default function CategoriesRollup({ range, outletIds: parentOutletIds, compareMode = "same_period" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCat, setOpenCat] = useState({});
  const [localOutletIds, setLocalOutletIds] = useState([]);
  // Local picker overrides the parent's global store filter for THIS panel
  const effectiveOutletIds = localOutletIds.length ? localOutletIds : (parentOutletIds || []);

  useEffect(() => {
    if (!range) return;
    let cancel = false;
    setLoading(true);
    const params = { start_date: range.start, end_date: range.end };
    if (effectiveOutletIds.length) params.outlet_ids = effectiveOutletIds.join(",");
    api.get("/metrics/category-rollup", { params })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range?.start, range?.end, effectiveOutletIds.join(",")]);

  const rows = data?.rows || [];
  const total = data?.total_revenue || 0;

  const buildExport = () => {
    const columns = [
      { key: "n", label: "#", align: "right" },
      { key: "category", label: "Category" },
      { key: "sku_count", label: "SKUs", align: "right" },
      { key: "units", label: "Units", align: "right" },
      { key: "growth", label: "Δ vs prior", align: "right" },
      { key: "revenue", label: "Revenue", align: "right" },
      { key: "share", label: "Share %", align: "right" },
    ];
    const flat = rows.map((r, i) => ({
      n: i + 1,
      category: r.category,
      sku_count: r.sku_count,
      units: fmt.int(r.units),
      growth: r.unit_growth_pct != null ? fmt.pct1(r.unit_growth_pct) : "",
      revenue: fmt.money0(r.revenue),
      share: total ? ((r.revenue / total) * 100).toFixed(1) : "0.0",
    }));
    return {
      filenameBase: `categories-${range?.label || "range"}`,
      meta: { title: "Category mix", subtitle: `${range?.label || ""} · total $${Math.round(total).toLocaleString()}` },
      columns,
      rows: flat,
    };
  };

  const toggle = (row) => {
    if (!row.clickable) return;
    setOpenCat((prev) => ({ ...prev, [row.match_value]: !prev[row.match_value] }));
  };

  return (
    <div className="border border-[#0a0a0a] bg-[#ede8dc] p-6" data-testid="categories-rollup">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="bm-eyebrow inline-flex items-center gap-1 text-neutral-500 mb-1">
            <Layers size={11} /> Table 3 · Category mix
          </div>
          <h3 className="bm-headline text-3xl">Categories.</h3>
          <PeriodBadge range={range} className="mt-1" />
          <p className="bm-eyebrow text-neutral-500 mt-1">
            Top-level Lightspeed categories · Δ vs prior {windowPhrase(range?.start, range?.end)} · click a row to see its weekly per-store curve + top SKUs
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <LocalStorePicker
            value={localOutletIds}
            onChange={setLocalOutletIds}
            testId="categories-stores"
          />
          <ExportButtons
            disabled={!rows.length}
            testIdPrefix="categories-export"
            onCSV={() => { const e = buildExport(); downloadCSV(e.filenameBase, e.columns, e.rows); }}
            onPDF={() => { const e = buildExport(); downloadPDF(e.filenameBase, e.meta, e.columns, e.rows); }}
          />
          <div className="bm-eyebrow text-neutral-500">
            Dollar sales · net of tax
            {total > 0 && (
              <> · total <span className="text-[#0a0a0a] font-mono-bm">${Math.round(total).toLocaleString()}</span></>
            )}
          </div>
        </div>
      </div>

      {loading && !data && <div className="text-sm text-neutral-500">Loading…</div>}

      {rows.length > 0 && (
        <div className="border border-[#0a0a0a]/20" data-testid="categories-rollup-rows">
          {rows.map((r, i) => {
            const open = !!openCat[r.match_value];
            const share = total ? (r.revenue / total) * 100 : 0;
            const mobileRow = (
              <div className="md:hidden w-full flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <ChevronRight size={14} className={`shrink-0 text-neutral-500 transition-transform ${r.clickable ? "" : "opacity-0"} ${open ? "rotate-90" : ""}`} />
                  <span className="font-medium truncate">{r.category}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Delta pct={r.unit_growth_pct} />
                  <span className="font-mono-bm tabular-nums font-semibold">{fullMoney(r.revenue)}</span>
                </div>
              </div>
            );
            const commonRow = (
              <>
                {mobileRow}
                <div className="hidden md:flex items-center gap-3 w-full">
                <ChevronRight
                  size={14}
                  className={`shrink-0 text-neutral-500 transition-transform ${
                    r.clickable ? "" : "opacity-0"
                  } ${open ? "rotate-90" : ""}`}
                />
                <span className="bm-eyebrow font-mono-bm text-neutral-500 w-8">
                  #{String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-medium flex-1 truncate">
                  {r.category}
                  {!r.clickable && (
                    <span className="bm-eyebrow ml-2 px-1.5 py-0.5 border border-[#0a0a0a]/30 text-[9px] text-neutral-500">
                      see Printed Tees page
                    </span>
                  )}
                </span>
                <span className="bm-eyebrow px-1.5 py-0.5 border border-[#0a0a0a]/30 text-[9px]">
                  {r.sku_count} SKU{r.sku_count === 1 ? "" : "s"}
                </span>
                <span className="font-mono-bm tabular-nums w-20 text-right text-sm">
                  {r.units.toLocaleString()}u
                </span>
                <span className="w-20 text-right"><Delta pct={r.unit_growth_pct} /></span>
                <span className="font-mono-bm tabular-nums w-24 text-right font-semibold">
                  {fullMoney(r.revenue)}
                </span>
                <span className="bm-eyebrow font-mono-bm text-neutral-500 w-10 text-right tabular-nums">
                  {share.toFixed(1)}%
                </span>
                </div>
              </>
            );
            return (
              <div key={`${r.match_field}-${r.match_value}`} className="border-b border-[#0a0a0a]/15 last:border-b-0">
                {r.clickable ? (
                  <button
                    onClick={() => toggle(r)}
                    className={`w-full flex items-center gap-3 px-3 py-3 md:py-2 text-left hover:bg-[#0a0a0a]/5 ${open ? "bg-[#0a0a0a]/5" : ""}`}
                    data-testid={`cat-rollup-toggle-${i}`}
                    aria-expanded={open}
                  >
                    {commonRow}
                  </button>
                ) : (
                  <div
                    className="w-full flex items-center gap-3 px-3 py-3 md:py-2 text-left cursor-default opacity-85"
                    data-testid={`cat-rollup-row-${i}`}
                  >
                    {commonRow}
                  </div>
                )}

                {r.clickable && open && (
                  <div className="bg-[#f5f2eb] border-t border-[#0a0a0a]/15" data-testid={`cat-rollup-body-${i}`}>
                    <SubCategoryTrend
                      category={r.match_value}
                      range={range}
                      outletIds={effectiveOutletIds}
                      compareMode={compareMode}
                    />
                    <CategorySkuTable
                      category={r.match_value}
                      range={range}
                      outletIds={effectiveOutletIds}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
